/**
 * MetaPillState — the All / None / specific-items model shared by every Crop
 * Circle filter that offers an "All" + "(not grouped)" / "(not labeled)" + items
 * selector (the Groups and Labels sections).
 *
 * Centralising the transitions, the count, and the server-filter translation
 * here keeps the four consumers from drifting: the filter-panel UI
 * (shared-ui `MetaFilterPills`), the persisted filter models
 * (`ActivityFilterModel` / `AssetFilterModel`), and the home dashboard
 * (`useHomeDashboardFilters`).
 *
 *  - `includeAll`  → the "All" pill.
 *  - `includeNone` → the "(not grouped)" / "(not labeled)" pill.
 *  - `items`       → specific group / label ids.
 *
 * Default = both meta pills on, no items (show everything).
 */
export interface MetaPillState {
  includeAll: boolean;
  includeNone: boolean;
  items: string[];
}

export const DEFAULT_META_PILL_STATE: MetaPillState = {
  includeAll: true,
  includeNone: true,
  items: [],
};

/**
 * Toggle the "All" (`which='all'`) or "(not …)" (`which='none'`) meta pill.
 *  - Turning one OFF is a no-op when it is the last active selection (never a
 *    silent flip to the other pill).
 *  - Turning one ON supersedes any specific-item selection (clears items) and
 *    leaves the other pill untouched.
 */
export const toggleMetaPill = (
  state: MetaPillState,
  which: 'all' | 'none'
): MetaPillState => {
  const isOn = which === 'all' ? state.includeAll : state.includeNone;
  const otherOn = which === 'all' ? state.includeNone : state.includeAll;
  if (isOn) {
    // Turning OFF — keep it on when nothing else is selected (no silent flip).
    if (!otherOn && state.items.length === 0) return state;
    return which === 'all'
      ? { ...state, includeAll: false }
      : { ...state, includeNone: false };
  }
  // Turning ON — meta mode supersedes any specific selection.
  return which === 'all'
    ? { includeAll: true, includeNone: state.includeNone, items: [] }
    : { includeAll: state.includeAll, includeNone: true, items: [] };
};

/**
 * Toggle a specific item. Selecting one clears the meta pills; deselecting the
 * last one resets to the default (both meta pills on).
 */
export const toggleMetaItem = (
  state: MetaPillState,
  id: string
): MetaPillState => {
  if (state.items.includes(id)) {
    const items = state.items.filter((x) => x !== id);
    return items.length === 0 ? DEFAULT_META_PILL_STATE : { ...state, items };
  }
  return {
    includeAll: false,
    includeNone: false,
    items: state.items.concat(id),
  };
};

/**
 * Translate the pill state to the server fields a filter sends:
 *  - `ids`         → the specific group/label ids (`groupIds` / `labelIds`).
 *  - `includeNone` → the `includeUngrouped` / `includeUnlabeled` boolean.
 *
 * A field left `undefined` means "do not send it". The default state sends
 * nothing (legacy "show everything"). `includeAll` is intentionally never
 * emitted — it is a client-only UI marker, expressed server-side as
 * `includeNone: false` (every grouped/labelled item, excluding none). When it is
 * on it takes precedence over any stray specific items (a state the UI can't
 * reach, but defensively handled the same way the filter models always have).
 */
export const metaPillToFilter = (
  state: MetaPillState
): { ids?: string[]; includeNone?: boolean } => {
  const items = state.items.filter(Boolean);
  const isDefault = state.includeAll && state.includeNone && items.length === 0;
  if (isDefault) return {};
  if (state.includeAll) {
    // "All": every grouped/labelled item — explicitly exclude none.
    return { includeNone: false };
  }
  const out: { ids?: string[]; includeNone?: boolean } = {};
  if (items.length > 0) out.ids = items;
  if (state.includeNone) out.includeNone = true;
  return out;
};

/** Non-default selection count (drives the filter badge / panel title). */
export const metaPillCount = (state: MetaPillState): number => {
  const isDefault =
    state.includeAll && state.includeNone && state.items.length === 0;
  if (isDefault) return 0;
  return (
    state.items.length +
    (state.includeAll ? 1 : 0) +
    (state.includeNone ? 1 : 0)
  );
};
